"use client";

import { useMemo } from "react";
import { Vehicle, RENTAL_VEHICLES, DRIVER_VEHICLES } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import VehicleCard from "@/(global)/location/vehicules/components/VehicleCard";
import { FICHE_UI } from "./ficheUi";
import { tr } from "@/config/localeText";

type RecommendationCarProps = {
  vehicle: Vehicle;
  locale?: Locale;
};

// Fonction utilitaire pour trier par proximité de prix puis par nouveauté
const sortByPriceThenNew = (pivotPrice: number) => (a: Vehicle, b: Vehicle) => {
  const priceDiffA = Math.abs(a.price.amount - pivotPrice);
  const priceDiffB = Math.abs(b.price.amount - pivotPrice);
  if (priceDiffA !== priceDiffB) return priceDiffA - priceDiffB;
  return b.isNew ? 1 : -1;
};

// Fonction pour obtenir les recommandations
const getRecommendations = (current: Vehicle): Vehicle[] => {
  // Sélectionner le bon pool de véhicules en fonction du type
  const pool = current.withDriver ? DRIVER_VEHICLES : RENTAL_VEHICLES;

  // Filtrer l'inventaire pour exclure le véhicule courant
  const availablePool = pool.filter((v) => v.id !== current.id);

  // Étape A - Même marque
  const byBrand = availablePool
    .filter((v) => v.brand === current.brand)
    .sort(sortByPriceThenNew(current.price.amount));
  if (byBrand.length >= 3) return byBrand.slice(0, 3);

  // Étape B - Proches en prix
  const cheaper = availablePool
    .filter((v) => v.price.amount < current.price.amount)
    .sort(sortByPriceThenNew(current.price.amount));
  const pricier = availablePool
    .filter((v) => v.price.amount > current.price.amount)
    .sort(sortByPriceThenNew(current.price.amount));

  // Cas principal : 2 moins chers + 1 plus cher
  if (cheaper.length >= 2 && pricier.length >= 1) {
    return [cheaper[0], cheaper[1], pricier[0]];
  }

  // Fallback 1 : pas de plus cher
  if (pricier.length === 0 && cheaper.length >= 3) {
    return cheaper.slice(0, 3);
  }

  // Fallback 2 : pas de moins cher
  if (cheaper.length === 0 && pricier.length >= 3) {
    return pricier.slice(0, 3);
  }

  // Fallback 3 : compléter avec transmission similaire
  const byGearbox = availablePool
    .filter((v) => v.specs.gearbox === current.specs.gearbox)
    .sort(sortByPriceThenNew(current.price.amount));

  // Fusionner et dédupliquer
  const allOptions = [...cheaper, ...pricier, ...byGearbox];
  const uniqueOptions = allOptions.filter(
    (v, i) => allOptions.findIndex((x) => x.id === v.id) === i,
  );

  return uniqueOptions.slice(0, 3);
};

const SectionHeader = ({ locale }: { locale: Locale }) => {
  const t = tr(FICHE_UI, locale).reco;
  return (
    <div className="text-center">
      <h2 className="text-2xl font-light uppercase text-primaryLoc sm:text-3xl">
        {t.heading}
      </h2>
      <p className="mt-2 text-base text-primaryLoc/80 sm:text-lg">
        {t.subheading}
      </p>
    </div>
  );
};

export default function RecommendationCar({
  vehicle,
  locale = "fr",
}: RecommendationCarProps) {
  // Calculer les recommandations
  const recommendations = useMemo(() => getRecommendations(vehicle), [vehicle]);

  // Ne rien afficher si pas assez de recommandations
  if (recommendations.length === 0) return null;

  return (
    <section className="bg-white py-14 md:py-20">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <SectionHeader locale={locale} />

        <div className="mt-10 grid grid-cols-1 gap-6 md:mt-12 md:grid-cols-2 lg:grid-cols-3">
          {recommendations.map((v) => (
            <VehicleCard key={v.id} vehicle={v} locale={locale} />
          ))}
        </div>
      </div>
    </section>
  );
}
